/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import React, { useCallback, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import './EnergyTrendChart.css';
import { IconWithTooltip } from '../../globalUtils/globalIcons';
import { SelectDropDown, useAppDispatch, useAppSelector, useEffectOnce } from '../../globalUtils/globalExports';
import { getDashboardReducer, getReferenceDataReducer, setFilterData } from '../../rmsReduxStore/reduxExports';
import { format, parse, isValid } from 'date-fns';
import { RectangularSkeleton } from '../../globalUtils/SkeletonDesign/SkeletonDesign';

interface EnergyTrendProp {
  handleFilter: Function,
  getFilterValue: Function
}

const EnergyTrendChart: React.FC<EnergyTrendProp> = (props) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { handleFilter, getFilterValue } = props;
  const { intervalRangeReferenceData } = useAppSelector(getReferenceDataReducer);
  const [filterValue, setFilterValue] = useState<IGeneralReferenceData>()
  const dispatch = useAppDispatch();
  const navigationUpperLimit = 31
  const { newDashboardEnergyTrendInfo } = useAppSelector(getDashboardReducer);
  const [visibleMin, setVisibleMin] = useState(0);
  const [visibleMax, setVisibleMax] = useState(navigationUpperLimit);

  const dataLength = newDashboardEnergyTrendInfo?.length || 0;

  useEffectOnce(() => {
    setFilterValue(getFilterValue('energyConsumptionIntervalRange'))
  })

  const hasCustomerData = newDashboardEnergyTrendInfo?.some(entry =>
    Array.isArray(entry.customerWiseData) && entry.customerWiseData.length > 0
  );  

  const xAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject) {
    const rawValue = this.value;
    const label = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
    const [day, month] = label.split(' ');
  
    const chartWidth = this.axis.chart.chartWidth;
    const numCategories = this.axis.categories?.length || 1;
    const approxCategoryWidth = chartWidth / numCategories;
  
    const shouldWrap = approxCategoryWidth > 0; // adjust threshold if needed
    // const shouldWrap = true
  
    return shouldWrap
      ? `<div style="text-align: center;">${day}<br>${month}</div>`
      : `<div style="text-align: center;">${day}<br><span style="opacity: 0.6; font-size: 10px;">${month}</span></div>`;
  }; 

  const getFormattedData = useCallback(() => {
    const rawData = newDashboardEnergyTrendInfo || [];
  
    const slicedData = rawData.slice(visibleMin, visibleMax + 1);
    // console.log(slicedData)
  
    const xAxis = slicedData.map(item => formatTimeStamp(item.timeStamp));
    // console.log(xAxis)
  
    const customerNamesSet = new Set<string>();
    (newDashboardEnergyTrendInfo || []).forEach(item => {
      item.customerWiseData?.forEach(customer => {
        customerNamesSet.add(customer.name);
      });
    });

    function formatTimeStamp(rawTimeStamp: string): string {
      let dateObj;
    
      const timeMatch = rawTimeStamp.match(/^(\d{1,2})(am|pm)$/i);
      if (timeMatch) {
        const hours = parseInt(timeMatch[1], 10);
        const isPM = timeMatch[2].toLowerCase() === 'pm';
        const hour24 = isPM ? (hours === 12 ? 12 : hours + 12) : (hours === 12 ? 0 : hours);
        dateObj = new Date();
        dateObj.setHours(hour24, 0, 0, 0);
        return format(dateObj, 'h a'); // e.g. "9 AM"
      }
    
      // Try ISO date first (like '2025-05-28')
      dateObj = new Date(rawTimeStamp);
      if (isValid(dateObj)) {
        return format(dateObj, 'd MMM'); // e.g. "28 May"
      }
    
      // If not ISO, try 'd MMM' with current year
      const currentYear = new Date().getFullYear();
      dateObj = parse(`${rawTimeStamp} ${currentYear}`, 'd MMM yyyy', new Date());
      if (isValid(dateObj)) {
        return format(dateObj, 'd MMM'); // e.g. "23 Apr"
      }
      return rawTimeStamp;
    }
    
    const customerNames = Array.from(customerNamesSet);
  
    const series = customerNames.map(name => {
      return {
        name,
        type: 'line',
        data: slicedData.map(item => {
          const match = item.customerWiseData?.find(c => c.name === name);
          return match ? match.energyConsumed : 0;
        }),
        marker: false,
      };
    });
  
    return {
      xAxis,
      series,
    };
  }, [newDashboardEnergyTrendInfo, visibleMin, visibleMax]);

  let labelStep = 1;  //draft
  const { xAxis } = getFormattedData();
  const totalBars = xAxis.length;
  if (totalBars > 20) labelStep = 4;
  else if (totalBars > 10) labelStep = 2;
    
  const { series } = getFormattedData();
  const chartHeight = 400;

  const chartOptions: Highcharts.Options = {
    chart: {
      type: 'line',
      height: chartHeight,
      spacingTop: 10,
      spacingRight: 10,
      spacingBottom: 0,
      spacingLeft: 10,
      events: {
        render: function () {
          const chart = this as Highcharts.Chart & any;
  
          // Remove existing custom arrows before adding new ones
          const existingArrows = chart.container.getElementsByClassName('custom-arrow');
          while (existingArrows.length > 0) {
            existingArrows[0].remove();
          }
  
          // Left arrow (shifted more inside by changing left position)
          if (visibleMin > 0) {
            chart.renderer.image(
              '/images/left_arrow_highchart.svg',
              chart.plotLeft, // shifted inside
              chart.plotTop + chart.plotHeight / 2 - 10,
              20,
              20
            )
            .addClass('custom-arrow left')
            .on('click', () => handleNavigation('left'))
            .add();
          }
  
          // Right arrow (shifted more inside by changing left position)
          if (visibleMax < dataLength - 1) {
            chart.renderer.image(
              '/images/right_arrow_highchart.svg',
              chart.plotLeft + chart.plotWidth - 15, // shifted inside
              chart.plotTop + chart.plotHeight / 2 - 10,
              20,
              20
            )
            .addClass('custom-arrow right')
            .on('click', () => handleNavigation('right'))
            .add();
          }
        }
      },
    },
    title: { text: undefined },
    subtitle: { text: undefined },
    xAxis: {
      // title: {
      //   text: 'Month',
      // },
      categories: xAxis,
      lineColor: '#3347E6',
      tickLength: 5,
      gridLineWidth: 1,
      tickmarkPlacement: 'on',
      gridLineDashStyle: 'ShortDash',
      labels: {
        rotation: 0,
        useHTML: true,
        step: labelStep, 
        formatter: xAxisLabelFormatter,
      }
    },
    yAxis: {
      gridLineColor: '#D3D6DF',
      gridLineWidth: 1,
      gridLineDashStyle: 'ShortDash',
      title: {
        text: 'KWh',
      },
    },
    tooltip: {
      shared: false,
      valueDecimals: 2,
    },
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      itemWidth: 100,
      maxHeight: 60,
      navigation: {
        activeColor: '#000',
        inactiveColor: '#ccc',
        animation: true,
        arrowSize: 12,
      },
      itemStyle: {
        fontSize: '12px',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: '#000',  // legend text color
      },
      useHTML: true,
      labelFormatter: function () {
        const isVisible = this.visible !== false;
        const textStyle = isVisible ? '' : 'color: #999; text-decoration: line-through;';
        const boxColor = isVisible ? this.color : '#ccc';
        return `
          <span style="display: inline-flex; align-items: center; gap: 6px; ${textStyle}">
                      <span style="width: 10px; height: 10px; background-color: ${boxColor}; display: inline-block; border-radius: 50%;"></span>
            <span 
              title="${this.name}" 
              style="max-width: 50px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
              ${this.name}
            </span>
          </span>
        `;
      },
      // itemStyle: {
      //   fontSize: '12px',
      //   cursor: 'default',
      // },
      symbolWidth: 0,
    }, 
    series: getFormattedData().series as Highcharts.SeriesOptionsType[],
    credits: { enabled: false },
    exporting: { enabled: false },
  };

  const handleSelectChange = (val): void => {
    setFilterValue(val);
    handleEnergyTrendFilter(val, 'energyConsumptionIntervalRange');
    handleChartPageReset();
  };

  const handleChartPageReset = () => {
    setVisibleMin(0);
    setVisibleMax(navigationUpperLimit);
  }

  const handleNavigation = (direction: 'left' | 'right') => {
    const step = navigationUpperLimit + 1;
    if (direction === 'left') {
        setVisibleMin(Math.max(visibleMin - step, 0));
        setVisibleMax(Math.max(visibleMax - step, step));
    } else {
        setVisibleMin(Math.min(visibleMin + step, dataLength - (step + 1)));
        setVisibleMax(Math.min(visibleMax + step, dataLength - 1));
    }
  };

  const handleEnergyTrendFilter = useCallback(async (val: any, filterKey: string): Promise<void> => {
    const res = await setFilterData(val, filterKey, 'dashboardV2', 'SET', false)(dispatch)
    if (res?.message === 'Action dispatched successfully') {
        handleFilter(res?.filters);
    }
  }, [setFilterData])

  return (
    <div className="widget-wrap energy-chart-card">
      <div className="widget-header">
        <div className="widget-heading energy-trend-chart-header">
          Energy Consumption Trend by Customers
          <IconWithTooltip iconClassName='info-icon' toolTipText='Average energy consumed by customers' containerClassName={'active-alarm-tooltip'} />
        </div>
        <div className="chart-filters">
          {/* <select disabled>
            <option>Select Stations</option>
          </select > */}
          {/* <select disabled>
            <option>Monthly</option>
          </select> */}
          <SelectDropDown
            selectValue={filterValue}
            selectOptions={intervalRangeReferenceData ?? []}
            handleSelectOptionChange={handleSelectChange}
            selectDropDownId='alarm-failed-transaction-dropdown'
            dropDownClassName='alarm-failed-transaction-dropdown'
            isMultiSelect={false}
            placeholder={'Interval'}
          />          
        </div>
      </div>
      <div className="widget-body">
  <div className="highchart-wrap">
    {!newDashboardEnergyTrendInfo ? (
      <div style={{ height: `${chartHeight}px` }}>
        <RectangularSkeleton skeletonContainerClassName='' />
      </div>
    ) : hasCustomerData ? (
      <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
    ) : (
      <div className="no-data-text">No Data Available</div>
    )}
  </div>
</div>
    </div>
  );
};

export default EnergyTrendChart;